import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button, ButtonLink } from '@/design-system/Button'
import { Card, Overline, Section, SectionTitle } from '@/design-system/primitives'
import { usePublicTicketTypes } from '@/features/tickets/api'
import { formatDateTime, formatTime } from '@/lib/datetime'
import { formatMoney } from '@/lib/money'

import type { PublicContentBlock, PublicEvent, PublicSponsor } from '../types'
import { readBooleanSetting, readI18nSetting } from '../types'
import { WhatsAppLink } from './WhatsAppLink'

/** Bloc éditorial : tout son contenu vient de l'administration. */
export function ContentSection({
  block,
  anchor,
}: {
  block: PublicContentBlock
  anchor?: string
}) {
  return (
    // Un bloc éditorial est souvent court — deux phrases. Il respire moins
    // qu'une section à part entière, sinon deux paragraphes se retrouvent
    // séparés par un écran de vide.
    <Section id={anchor ?? block.key} className="!py-8 sm:!py-10">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] lg:items-center">
        <div>
          <Overline>{block.subtitle || block.key.replace(/_/g, ' ')}</Overline>
          <SectionTitle>{block.title}</SectionTitle>
          {block.body ? (
            <p className="mt-5 whitespace-pre-line text-body-lg text-content-secondary">
              {block.body}
            </p>
          ) : null}
          {block.cta_url && block.cta_label ? (
            <ButtonLink
              variant="outline"
              className="mt-6"
              href={block.cta_url}
              target="_blank"
              rel="noreferrer noopener"
            >
              {block.cta_label}
            </ButtonLink>
          ) : null}
        </div>

        {block.image_url ? (
          <img
            src={block.image_url}
            alt=""
            className="w-full rounded-lg border border-line-subtle object-cover"
            loading="lazy"
          />
        ) : null}
      </div>
    </Section>
  )
}

/** Informations pratiques : date, horaires, lieu — dans le fuseau de l'événement. */
export function PracticalSection({ event }: { event: PublicEvent }) {
  const { t, i18n } = useTranslation()
  const address = [event.address_line1, event.postal_code, event.city].filter(Boolean).join(', ')

  const rows = [
    event.doors_open_at && {
      label: t('event.doors'),
      value: formatTime(event.doors_open_at, i18n.language, event.timezone),
    },
    {
      label: t('event.starts'),
      value: formatDateTime(event.starts_at, i18n.language, event.timezone),
    },
    event.ends_at && {
      label: t('event.ends'),
      value: formatTime(event.ends_at, i18n.language, event.timezone),
    },
    (event.venue_name || address) && {
      label: t('event.venue'),
      value: [event.venue_name, address].filter(Boolean).join(' — '),
    },
  ].filter(Boolean) as { label: string; value: string }[]

  return (
    <Section id="infos">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {rows.map((row) => (
          <Card key={row.label}>
            <p className="overline text-content-muted">{row.label}</p>
            <p className="mt-2 text-content-primary">{row.value}</p>
          </Card>
        ))}
      </div>
    </Section>
  )
}

/**
 * Billetterie sur la page d'accueil.
 *
 * Aperçu réel des billets en vente — noms, prix et disponibilité viennent de
 * l'API. L'achat lui-même se fait sur `/billets`, où le serveur calcule le
 * devis à chaque modification du panier.
 */
export function TicketsSection({ event }: { event: PublicEvent }) {
  const { t, i18n } = useTranslation()
  const ticketTypesQuery = usePublicTicketTypes(event.slug)
  const ticketTypes = ticketTypesQuery.data ?? []

  const open = readBooleanSetting(event.settings, 'sales.open', false)
  // Pas de repli sur une traduction figée : ce texte est une décision
  // commerciale, il vient du back-office ou il ne s'affiche pas. Le repli
  // ressuscitait une phrase que l'équipe avait justement retirée — et que
  // l'événement contredit, puisque les tables comprennent l'entrée.
  const notice = readI18nSetting(
    event.settings,
    'notice.admission_not_included',
    i18n.language,
    event.locale,
  )

  const onSale = ticketTypes.filter((ticketType) => ticketType.on_sale)
  const purchasable = open && onSale.length > 0

  return (
    <Section id="tickets">
      <Overline>{t('sections.tickets')}</Overline>
      <SectionTitle>{purchasable ? t('tickets.openTitle') : t('tickets.closedTitle')}</SectionTitle>
      <p className="mt-5 max-w-2xl text-body-lg text-content-secondary">
        {purchasable ? t('tickets.openBody') : t('tickets.closedBody')}
      </p>

      {ticketTypes.length > 0 ? (
        <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ticketTypes.map((ticketType) => (
            <li key={ticketType.id}>
              <Card interactive className="h-full">
                <div className="flex items-baseline justify-between gap-4">
                  <p className="font-display text-heading-md text-content-primary">
                    {ticketType.name}
                  </p>
                  <p className="shrink-0 tabular-nums text-champagne-300">
                    {formatMoney(
                      { amount_minor: ticketType.price_minor, currency: ticketType.currency },
                      i18n.language,
                    )}
                  </p>
                </div>
                <p className="mt-2 text-sm text-content-secondary">
                  {ticketType.admits_count > 1
                    ? t('ticketing.perPeople', { count: ticketType.admits_count })
                    : t('ticketing.perPerson')}
                </p>
                {ticketType.description ? (
                  <p className="mt-3 text-sm text-content-secondary">{ticketType.description}</p>
                ) : null}
                {ticketType.availability === 'sold_out' ? (
                  <p className="mt-4 text-sm text-content-muted">{t('ticketing.soldOut')}</p>
                ) : null}
              </Card>
            </li>
          ))}
        </ul>
      ) : null}

      {/* Le cadre suit le texte : sans lui, il ne reste qu'un rectangle vide,
          plus déroutant que l'absence de mention. */}
      {notice ? (
        <div className="glass mt-6 max-w-2xl rounded-lg px-6 py-5">
          <p className="text-sm text-content-secondary">⚠️ {notice}</p>
        </div>
      ) : null}

      <div className="mt-6 flex flex-wrap gap-3">
        {purchasable ? (
          <Link to="/billets">
            <Button size="lg">{t('cta.ticket')}</Button>
          </Link>
        ) : null}
      </div>
    </Section>
  )
}


export function SponsorsSection({ sponsors }: { sponsors: PublicSponsor[] }) {
  const { t } = useTranslation()
  if (sponsors.length === 0) return null

  return (
    <Section id="sponsors">
      <Overline>Partners</Overline>
      <SectionTitle>{t('sections.sponsors')}</SectionTitle>

      <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {sponsors.map((sponsor) => (
          <li key={sponsor.name}>
            <Card interactive>
              <div className="flex items-center gap-4">
                {sponsor.logo_url ? (
                  <img
                    src={sponsor.logo_url}
                    alt=""
                    className="h-12 w-12 rounded-full object-cover"
                    loading="lazy"
                  />
                ) : null}
                <div>
                  <p className="text-content-primary">
                    {sponsor.url ? (
                      <a href={sponsor.url} target="_blank" rel="noreferrer noopener">
                        {sponsor.name}
                      </a>
                    ) : (
                      sponsor.name
                    )}
                  </p>
                  {sponsor.description ? (
                    <p className="mt-1 text-sm text-content-secondary">{sponsor.description}</p>
                  ) : null}
                </div>
              </div>
            </Card>
          </li>
        ))}
      </ul>
    </Section>
  )
}

export function ContactSection({ event }: { event: PublicEvent }) {
  const { t } = useTranslation()
  const socials = Object.entries(event.social).filter(([, url]) => Boolean(url))

  return (
    <Section id="contact">
      <Overline>Contact</Overline>
      <SectionTitle>{t('sections.contact')}</SectionTitle>

      <div className="mt-6 flex flex-wrap gap-3">
        {event.contact_email ? (
          <ButtonLink variant="outline" href={`mailto:${event.contact_email}`}>
            {event.contact_email}
          </ButtonLink>
        ) : null}
        {event.contact_phone ? (
          <ButtonLink variant="outline" href={`tel:${event.contact_phone}`}>
            {event.contact_phone}
          </ButtonLink>
        ) : null}
        {/* À côté du téléphone : c'est par WhatsApp que la plupart des
            invités écrivent vraiment, et un message s'y envoie sans attendre
            qu'on décroche. */}
        <WhatsAppLink settings={event.settings} />
        {socials.map(([network, url]) => (
          <ButtonLink
            key={network}
            variant="ghost"
            href={url}
            target="_blank"
            rel="noreferrer noopener"
          >
            {network}
          </ButtonLink>
        ))}
      </div>
    </Section>
  )
}

